export interface ContactValues {
    name: string
    email: string
    phone: string
    topic: string
    message: string
}

export type ContactField = keyof ContactValues

export type ContactErrors = Partial<Record<ContactField, string>>

export const contactTopics = [
    'General enquiry',
    'Membership',
    'Tee times and visiting',
    'Events and weddings',
    'Academy and lessons',
    'Dining and the Lodges',
    'Press and partnerships',
    'Careers'
]

export const emptyContact = (): ContactValues => ({
    name: '',
    email: '',
    phone: '',
    topic: contactTopics[0],
    message: ''
})

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const limits: Record<ContactField, number> = {
    name: 120,
    email: 200,
    phone: 40,
    topic: 80,
    message: 4000
}

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

/** Coerces untrusted input (a request body) into clean contact values. */
export const normaliseContact = (input: unknown): ContactValues => {
    const source = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>

    return {
        name: asText(source.name),
        email: asText(source.email).toLowerCase(),
        phone: asText(source.phone),
        topic: asText(source.topic),
        message: asText(source.message)
    }
}

/** Shared by the contact form and its API route, so both give the same answers. */
export const validateContact = (values: ContactValues): ContactErrors => {
    const errors: ContactErrors = {}

    if (!values.name) errors.name = 'Please tell us your name.'
    if (!values.email) errors.email = 'We need an email address to reply to.'
    else if (!EMAIL_PATTERN.test(values.email)) errors.email = 'That email address doesn’t look quite right.'
    if (!contactTopics.includes(values.topic)) errors.topic = 'Please choose a topic.'
    if (!values.message) errors.message = 'Please write a short message.'
    else if (values.message.length < 10) errors.message = 'Could you tell us a little more?'

    for (const field of Object.keys(limits) as ContactField[]) {
        if (values[field].length > limits[field] && !errors[field]) {
            errors[field] = `Please keep this under ${limits[field]} characters.`
        }
    }

    return errors
}
